import { useState } from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Button, Checkbox, Dialog, Input, Textarea } from '@artosera/ui';
import { partiesLocales } from '@artosera/shared';
import {
  priseRdvApi,
  useActionRdv,
  useCreneauxRdv,
  useReservationRdv,
  type GesteRdv,
  type ReservationRdv,
} from '../../api/priseRendezVous.js';
import { galerieALaction, galerieAModule } from '../../auth/sujet.js';
import { useShellContext } from '../shell/context.js';
import { ErreurRdv } from './ErreurRdv.js';
import { ajouterJoursRdv, dateHeureRdv, lireSaisieRdv, periodeRdv } from './temps.js';
import styles from './RendezVous.module.css';

export function ReservationPanneau({
  id,
  fuseau,
  onFermer,
}: {
  id: string;
  fuseau: string;
  onFermer: () => void;
}) {
  const { galerieSlug, galerie } = useShellContext();
  const { t, i18n } = useTranslation(['regie', 'commun']);
  const fiche = useReservationRdv(galerieSlug, id);
  const [geste, setGeste] = useState<GesteRdv | null>(null);
  const r = fiche.data?.reservation;
  const langue = i18n.language === 'en' ? 'en' : 'fr';
  const actif = r && ['demandee', 'confirmee'].includes(r.etat);
  return (
    <Dialog
      open
      onOpenChange={(v) => {
        if (!v) onFermer();
      }}
      title={r?.numero ?? t('regie:priseRendezVous.reservation')}
      description={r?.visiteur.nom || t('regie:priseRendezVous.reservation')}
      closeLabel={t('commun:actions.fermer')}
    >
      <div className={styles.panneau}>
        {fiche.isLoading ? <p role="status">{t('commun:chargement')}</p> : null}
        <ErreurRdv erreur={fiche.error} />
        {r ? (
          <>
            <h2>{r.typeTitre[langue] || r.typeTitre.fr}</h2>
            <p>
              {dateHeureRdv(r.debut, fuseau)} – {partiesLocales(new Date(r.fin), fuseau).heure} ·{' '}
              {fuseau}
            </p>
            <p>
              {t(`regie:priseRendezVous.etats.${r.etat}`)} · {r.membreNom}
            </p>
            {r.contactEfface ? (
              <p>{t('regie:priseRendezVous.contactEfface')}</p>
            ) : (
              <div className={styles.actions}>
                <a href={`mailto:${r.visiteur.email}`}>{r.visiteur.email}</a>
                {r.visiteur.telephone ? (
                  <a href={`tel:${r.visiteur.telephone}`}>{r.visiteur.telephone}</a>
                ) : null}
              </div>
            )}
            <p>{r.visiteur.message}</p>
            <p>{r.message}</p>
            <p>
              {t('regie:priseRendezVous.consentement')} : {r.consentement.version} ·{' '}
              {dateHeureRdv(r.consentement.accordeLe, fuseau)}
            </p>
            <p>
              {t('regie:priseRendezVous.newsletter')} :{' '}
              {t(r.newsletter ? 'regie:priseRendezVous.oui' : 'regie:priseRendezVous.non')}
            </p>
            {r.agendaModifie ? (
              <p role="status">{t('regie:priseRendezVous.agendaModifie')}</p>
            ) : null}
            <nav className={styles.actions} aria-label={t('regie:priseRendezVous.liens')}>
              {r.contactId && galerieALaction(galerie, 'contacts:read') ? (
                <Link to={`/${galerieSlug}/contacts/${encodeURIComponent(r.contactId)}`}>
                  {t('regie:priseRendezVous.contact')}
                </Link>
              ) : null}
              {r.oeuvreId && galerieALaction(galerie, 'artworks:read') ? (
                <Link to={`/${galerieSlug}/oeuvres/${encodeURIComponent(r.oeuvreId)}`}>
                  {r.oeuvreTitre || t('regie:priseRendezVous.oeuvre')}
                </Link>
              ) : null}
              {r.rendezVousId &&
              galerieAModule(galerie, 'agenda-presse') &&
              galerieALaction(galerie, 'agenda:read') ? (
                <Link to={`/${galerieSlug}/agenda`}>{t('regie:priseRendezVous.agenda')}</Link>
              ) : null}
            </nav>
            {actif && galerieALaction(galerie, 'creneaux:write') ? (
              <div className={styles.actions}>
                {(r.etat === 'demandee'
                  ? (['valider', 'refuser', 'annuler', 'deplacer'] as const)
                  : (['annuler', 'deplacer'] as const)
                ).map((g) => (
                  <Button key={g} variant="secondary" onClick={() => setGeste(g)}>
                    {t(`regie:priseRendezVous.gestes.${g}`)}
                  </Button>
                ))}
              </div>
            ) : null}
            <h2>{t('regie:priseRendezVous.historique')}</h2>
            <ol>
              {r.historique.map((h, index) => (
                <li key={index}>
                  {dateHeureRdv(h.le, fuseau)} ·{' '}
                  {h.de ? `${t(`regie:priseRendezVous.etats.${h.de}`)} → ` : ''}
                  {t(`regie:priseRendezVous.etats.${h.vers}`)}{' '}
                  {h.motif === 'report_force' ? t('regie:priseRendezVous.reportForce') : h.motif}
                </li>
              ))}
            </ol>
            {geste ? (
              <GesteDialog
                key={geste}
                reservation={r}
                geste={geste}
                fuseau={fuseau}
                onFermer={() => setGeste(null)}
              />
            ) : null}
          </>
        ) : null}
      </div>
    </Dialog>
  );
}
function GesteDialog({
  reservation: r,
  geste,
  fuseau,
  onFermer,
}: {
  reservation: ReservationRdv;
  geste: GesteRdv;
  fuseau: string;
  onFermer: () => void;
}) {
  const { galerieSlug, galerie } = useShellContext();
  const { t } = useTranslation(['regie', 'commun']);
  const [message, setMessage] = useState('');
  const [date, setDate] = useState(() => partiesLocales(new Date(), fuseau).date);
  const [debut, setDebut] = useState('');
  const [force, setForce] = useState(false);
  const [libre, setLibre] = useState('');
  const [confirmerForce, setConfirmerForce] = useState(false);
  const periode = periodeRdv(date, 7, fuseau);
  const creneaux = useCreneauxRdv(
    galerieSlug,
    geste === 'deplacer' ? r.typeId : '',
    periode.du,
    periode.au,
    r.id,
  );
  const debutChoisi = force ? lireSaisieRdv(libre, fuseau)! : debut;
  const mutation = useActionRdv(galerieSlug, (_: void, appel) =>
    priseRdvApi.geste(
      galerieSlug,
      r.id,
      geste,
      geste === 'deplacer'
        ? { version: r.version, debut: debutChoisi, force }
        : { version: r.version, message },
      appel,
    ),
  );
  const peutForcer = galerieALaction(galerie, 'creneaux:configurer');
  const valide =
    geste !== 'deplacer' ||
    (force
      ? peutForcer && confirmerForce && !!lireSaisieRdv(libre, fuseau)
      : !!debut && !!creneaux.data?.creneaux.some((c) => c.debut === debut));
  return (
    <Dialog
      open
      onOpenChange={(v) => {
        if (!v) onFermer();
      }}
      title={t(`regie:priseRendezVous.gestes.${geste}`)}
      description={r.numero}
      closeLabel={t('commun:actions.fermer')}
      footer={
        <Button
          disabled={!valide}
          loading={mutation.isPending}
          onClick={() =>
            void mutation
              .mutateAsync()
              .then(onFermer)
              .catch(() => {
                setDebut('');
                setConfirmerForce(false);
              })
          }
        >
          {t('regie:priseRendezVous.confirmer')}
        </Button>
      }
    >
      <div className={styles.formulaire}>
        {geste === 'deplacer' ? (
          <>
            <p>{fuseau}</p>
            <div className={styles.actions}>
              <Button
                variant="secondary"
                disabled={!periode.du}
                onClick={() => {
                  setDate(ajouterJoursRdv(date, -7));
                  setDebut('');
                }}
              >
                {t('regie:priseRendezVous.semainePrecedente')}
              </Button>
              <label className={styles.champ}>
                {t('regie:priseRendezVous.semaine')}
                <Input
                  type="date"
                  required
                  value={date}
                  onChange={(e) => {
                    setDate(e.target.value);
                    setDebut('');
                  }}
                />
              </label>
              <Button
                variant="secondary"
                disabled={!periode.du}
                onClick={() => {
                  setDate(ajouterJoursRdv(date, 7));
                  setDebut('');
                }}
              >
                {t('regie:priseRendezVous.semaineSuivante')}
              </Button>
            </div>
            {creneaux.isLoading ? <p role="status">{t('commun:chargement')}</p> : null}
            <ErreurRdv erreur={creneaux.error} />
            {!creneaux.isLoading && !creneaux.error && !creneaux.data?.creneaux.length ? (
              <p>{t('regie:priseRendezVous.aucunCreneau')}</p>
            ) : null}
            <fieldset className={styles.bloc} disabled={force || creneaux.isFetching}>
              <legend>{t('regie:priseRendezVous.creneaux')}</legend>
              {creneaux.data?.creneaux.map((c) => (
                <label key={c.debut}>
                  <input
                    type="radio"
                    name="debut-rdv"
                    value={c.debut}
                    checked={debut === c.debut}
                    onChange={() => setDebut(c.debut)}
                  />{' '}
                  {dateHeureRdv(c.debut, fuseau)}
                </label>
              ))}
            </fieldset>
            {peutForcer ? (
              <>
                <Checkbox
                  label={t('regie:priseRendezVous.forcer')}
                  checked={force}
                  onCheckedChange={(v) => {
                    setForce(v === true);
                    setConfirmerForce(false);
                  }}
                />
                {force ? (
                  <>
                    <label className={styles.champ}>
                      {t('regie:priseRendezVous.nouveauDebut')}
                      <Input
                        type="datetime-local"
                        required
                        value={libre}
                        onChange={(e) => {
                          setLibre(e.target.value);
                          setConfirmerForce(false);
                        }}
                      />
                    </label>
                    <p>{t('regie:priseRendezVous.forceAide')}</p>
                    <Checkbox
                      label={t('regie:priseRendezVous.confirmerForce')}
                      checked={confirmerForce}
                      onCheckedChange={(v) => setConfirmerForce(v === true)}
                    />
                  </>
                ) : null}
              </>
            ) : null}
          </>
        ) : (
          <label className={styles.champ}>
            {t('regie:priseRendezVous.message')}
            <Textarea
              maxLength={2000}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
            />
          </label>
        )}
        <ErreurRdv erreur={mutation.error} />
      </div>
    </Dialog>
  );
}
